import { getApiEndpoint } from '@/config/api';

export interface Banner {
  id: number;
  name: string;
  type: 'principal' | 'destaque' | 'secundario';
  image_path: string;
  background_color?: string | null;
  icon?: string | null;
  title?: string | null;
  subtitle?: string | null;
  text?: string | null;
  link?: string | null;
  situacao: 'cadastrado' | 'publicado';
  status: 'ativo' | 'inativo';
  start_date?: string | null;
  end_date?: string | null;
  clicks?: number; // Contador de cliques
  views?: number; // Contador de impressões
  created_by?: number | null;
  created_by_name?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateBannerData {
  name: string;
  type: 'principal' | 'destaque' | 'secundario';
  image_path: string;
  background_color?: string | null;
  icon?: string | null;
  title?: string | null;
  subtitle?: string | null;
  text?: string;
  link?: string;
  situacao?: 'cadastrado' | 'publicado';
  status?: 'ativo' | 'inativo';
  start_date?: string | null;
  end_date?: string | null;
}

export interface UpdateBannerData {
  name?: string;
  type?: 'principal' | 'destaque' | 'secundario';
  image_path?: string;
  background_color?: string | null;
  icon?: string | null;
  title?: string | null;
  subtitle?: string | null;
  text?: string;
  link?: string;
  situacao?: 'cadastrado' | 'publicado';
  status?: 'ativo' | 'inativo';
  start_date?: string | null;
  end_date?: string | null;
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export const BannerService = {
  // Listar todos os banners (admin)
  async getAllBanners(): Promise<Banner[]> {
    try {
      const response = await fetch(getApiEndpoint('/api/banners'), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `Erro HTTP ${response.status}` }));
        console.error('Erro ao buscar banners:', errorData);
        throw new Error(errorData.error || `Erro ao buscar banners (${response.status})`);
      }

      const data = await response.json();
      return data;
    } catch (error: any) {
      console.error('Erro na requisição de banners:', error);
      throw error;
    }
  },

  // Buscar banners por tipo (público)
  async getBannersByType(type: 'principal' | 'destaque' | 'secundario'): Promise<Banner[]> {
    const response = await fetch(getApiEndpoint(`/api/banners/public/${type}`));

    if (!response.ok) {
      throw new Error('Erro ao buscar banners');
    }

    return response.json();
  },

  // Criar banner
  async createBanner(data: CreateBannerData): Promise<Banner> {
    const response = await fetch(getApiEndpoint('/api/banners'), {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao criar banner');
    }

    return response.json();
  },

  // Atualizar banner
  async updateBanner(id: number, data: UpdateBannerData): Promise<Banner> {
    try {
      const response = await fetch(getApiEndpoint(`/api/banners/${id}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        let errorMessage = 'Erro ao atualizar banner';
        try {
          const error = await response.json();
          errorMessage = error.error || errorMessage;
        } catch (e) {
          errorMessage = `Erro HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      return response.json();
    } catch (error: any) {
      console.error('Erro detalhado ao atualizar banner:', error);
      throw error;
    }
  },

  // Deletar banner
  async deleteBanner(id: number): Promise<void> {
    const response = await fetch(getApiEndpoint(`/api/banners/${id}`), {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao deletar banner');
    }
  },

  // Upload de imagem
  async uploadBannerImage(file: File): Promise<{ path: string }> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('authToken');
    const response = await fetch(getApiEndpoint('/api/upload/banner'), {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Erro ao fazer upload da imagem');
    }

    return response.json();
  },

  // Registrar clique em banner
  async registerClick(bannerId: number): Promise<void> {
    try {
      await fetch(getApiEndpoint(`/api/banners/click/${bannerId}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Erro ao registrar clique no banner:', error);
      // Não lançar erro para não interromper a navegação
    }
  },

  // Registrar impressão (view) do banner
  async registerView(bannerId: number): Promise<void> {
    try {
      await fetch(getApiEndpoint(`/api/banners/view/${bannerId}`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
    } catch (error) {
      console.error('Erro ao registrar impressão do banner:', error);
      // Não lançar erro para não interromper a exibição
    }
  },
};

